import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DbEvent } from '../types';

interface CalendarProps {
  events: DbEvent[];
  onEventClick: (event: DbEvent) => void;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function statusColor(status: string) {
  if (status === 'completed') return 'bg-green-400';
  if (status === 'cancelled') return 'bg-red-400';
  return 'bg-gold-500';
}

export function Calendar({ events, onEventClick }: CalendarProps) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const eventsForDay = (day: number): DbEvent[] => {
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return events.filter(e => e.event_date === dateStr);
  };

  const isToday = (day: number) =>
    day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft size={18} />
        </button>
        <span className="font-serif font-semibold text-gray-900">
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-100">
        {DAYS.map(d => (
          <div key={d} className="text-center text-xs font-medium text-gray-400 py-2">
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          const dayEvents = day ? eventsForDay(day) : [];
          return (
            <div
              key={i}
              className={`min-h-[52px] p-1 border-b border-r border-gray-50 last:border-r-0 ${
                !day ? 'bg-gray-50/50' : ''
              }`}
            >
              {day && (
                <>
                  <span
                    className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full mb-0.5 ${
                      isToday(day)
                        ? 'bg-gold-500 text-white'
                        : 'text-gray-700'
                    }`}
                  >
                    {day}
                  </span>
                  <div className="flex flex-col gap-0.5">
                    {dayEvents.slice(0, 2).map(ev => (
                      <button
                        key={ev.id}
                        onClick={() => onEventClick(ev)}
                        className={`w-full text-left truncate text-[10px] font-medium px-1 py-0.5 rounded text-white leading-tight ${statusColor(ev.status)}`}
                      >
                        {ev.clients?.name ?? 'Event'}
                      </button>
                    ))}
                    {dayEvents.length > 2 && (
                      <span className="text-[10px] text-gray-400 px-1">+{dayEvents.length - 2}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
